import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ValuationService } from '@/services/valuation/valuationService';
import { 
  ValuationRequest, 
  ValuationRequestPublic,
  ValuationRequestAuthenticated,
  ValuationResponse, 
  VehicleEstimationRequest,
  VehicleEstimationResponse,
} from '@/types/valuation';

interface AutoUncleCarResponse {
  car: {
    year: number;
    brand: string;
    car_model: string;
    hp?: number;
    body?: string;
    lpn: string;
    vin: string;
    fuel?: string;
    engineVolume?: string;
    color?: string;
    [key: string]: any;
  };
}

export const useValuation = () => {

  /**
   * Get vehicle information mutation
   */
  const getVehicleInformationMutation = useMutation({
    mutationFn: ({
      lpn,
      turnstileToken,
    }: {
      lpn: string;
      turnstileToken?: string;
    }) => ValuationService.getVehicleInformation(lpn, turnstileToken),
  });

  /**
   * Get registration date mutation
   */
  const getRegistrationDateMutation = useMutation({
    mutationFn: (lpn: string) => ValuationService.getRegistrationDate(lpn),
  });

  /**
   * Create valuation mutation for authenticated users
   */
  const createValuationMutation = useMutation({
    mutationFn: (valuationData: ValuationRequestAuthenticated) => 
      ValuationService.createValuation(valuationData),
    onSuccess: () => {
      // Invalidate any related queries if needed in the futureπ
      // For now, valuations are created independently
      // queryClient.invalidateQueries({ queryKey: ['valuations'] });
    },
  });

  /**
   * Create valuation mutation for public users
   */
  const createValuationPublicMutation = useMutation({
    mutationFn: (valuationData: ValuationRequestPublic) => 
      ValuationService.createValuationPublic(valuationData),
    onSuccess: () => {
      // Invalidate any related queries if needed in the future
      // For now, valuations are created independently
      // queryClient.invalidateQueries({ queryKey: ['valuations'] });
    },
  });

  /**
   * Get vehicle estimation mutation
   */
  const getVehicleEstimationMutation = useMutation({
    mutationFn: (request: VehicleEstimationRequest) =>
      ValuationService.getVehicleEstimation(request),
  });

  return {
    getVehicleInformationMutation,
    getVehicleInformationAsync: getVehicleInformationMutation.mutateAsync,
    isGettingVehicleInformation: getVehicleInformationMutation.isPending,
    vehicleInformationError: getVehicleInformationMutation.error,
    vehicleInformationData: getVehicleInformationMutation.data,
    getRegistrationDateMutation,
    getRegistrationDateAsync: getRegistrationDateMutation.mutateAsync,
    isGettingRegistrationDate: getRegistrationDateMutation.isPending,
    registrationDateError: getRegistrationDateMutation.error,
    registrationDateData: getRegistrationDateMutation.data,
    createValuationMutation,
    createValuationAsync: createValuationMutation.mutateAsync,
    isCreatingValuation: createValuationMutation.isPending,
    createValuationError: createValuationMutation.error,
    valuationData: createValuationMutation.data,
    createValuationPublicMutation,
    createValuationPublicAsync: createValuationPublicMutation.mutateAsync,
    isCreatingValuationPublic: createValuationPublicMutation.isPending,
    createValuationPublicError: createValuationPublicMutation.error,
    valuationPublicData: createValuationPublicMutation.data,
    getVehicleEstimationAsync: getVehicleEstimationMutation.mutateAsync,
    isGettingEstimation: getVehicleEstimationMutation.isPending,
    estimationError: getVehicleEstimationMutation.error,
    estimationData: getVehicleEstimationMutation.data,
  };
};
